import { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth, useContent } from '@/stores/RootStore';
import { authApi } from '@/services/authApi';
import SubPageHero from '@/components/SubPageHero';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { AsStepper, AsTimeline, AsHero, AsDomainBadge, AsStatusBadge, type AsLogRow } from './AsShared';
import { asLabel, parsePlan, activeStageKeys } from './asMeta';
import s from './As.module.scss';

interface AsRow { id: number; domain: string; product_name: string; status: string; stage_plan: string | null; plan_locked_at: string | null; created_at: string; updated_at: string; completed_at: string | null; }
interface AsDetail { id: number; domain: string; product_name: string; product_model: string | null; symptom: string;
	company_name: string | null; address: string | null; purchase_source: string | null; install_date: string | null;   // [2026-09-12 폼개편]
	contact_name: string | null; contact_phone: string | null; status: string; stage_plan: string | null; plan_locked_at: string | null; created_at: string;
	visit_date: string | null; warranty: string | null; technician: string | null;
	completed_at: string | null; confirmed_at: string | null; }
const warrantyLabel = (w: string | null) => (w === 'FREE' ? '무상' : w === 'PAID' ? '유상' : '-');

const dt = (v: string | null) => (v ? String(v).slice(0, 16).replace('T', ' ') : '-');

/**
 * [2026-09-12] 마이페이지 A/S 현황 — 내 A/S 목록 + 상세(택배 배송조회식 스테퍼·타임라인).
 *   상세에서 고객이 추가 메시지를 남기면 서버 타임라인에 기록되어 관리자와 유기적으로 공유된다.
 */
function MyAs({ embedded }: { embedded?: boolean }) {
	const auth = useAuth();
	const { company } = useContent();   // 대표 A/S 연락처 안내용
	const nav = useNavigate();
	const [rows, setRows] = useState<AsRow[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(false);
	const [sel, setSel] = useState<AsDetail | null>(null);
	const [logs, setLogs] = useState<AsLogRow[]>([]);
	const [note, setNote] = useState('');
	const [msg, setMsg] = useState<string | null>(null);
	const [cancelOpen, setCancelOpen] = useState(false);
	const [from, setFrom] = useState('');
	const [to, setTo] = useState('');
	const [period, setPeriod] = useState('');
	const size = 10;

	const load = useCallback(async () => {
		setLoading(true);
		const r = await authApi.asMine(page, size, from, to);
		setLoading(false);
		if (r.success) { setRows((r.items as AsRow[]) || []); setTotal(Number(r.total) || 0); }
	}, [page, from, to]);
	useEffect(() => { if (auth.isAuthed) load(); }, [load, auth.isAuthed]);

	const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
	function pickPeriod(mo: string) {
		setPeriod(mo); setPage(0);
		if (!mo) { setFrom(''); setTo(''); return; }
		const d = new Date(); d.setMonth(d.getMonth() - Number(mo));
		setFrom(iso(d)); setTo(iso(new Date()));
	}

	if (auth.ready && !auth.isAuthed) return <Navigate to="/login" replace />;

	async function open(id: number) {
		setMsg(null); setNote('');
		const r = await authApi.asDetail(id);
		if (r.success) { setSel(r.request as AsDetail); setLogs((r.timeline as AsLogRow[]) || []); }
		else setMsg(r.message || '불러오지 못했습니다.');
	}
	async function refresh(id: number) {
		const r = await authApi.asDetail(id);
		if (r.success) { setSel(r.request as AsDetail); setLogs((r.timeline as AsLogRow[]) || []); }
	}
	async function sendMsg() {
		if (!sel || !note.trim()) return;
		const r = await authApi.asMessage(sel.id, note.trim());
		if (r.success) { setNote(''); refresh(sel.id); } else setMsg(r.message || '실패했습니다.');
	}
	async function doCancel() {
		if (!sel) return;
		const r = await authApi.asCancel(sel.id);
		setCancelOpen(false);
		if (r.success) { refresh(sel.id); load(); } else setMsg(r.message || '취소할 수 없습니다.');
	}
	// [2026-09-12] 고객 수령·완료 확인(관리자 완료 후, 별개)
	async function doConfirm() {
		if (!sel) return;
		const r = await authApi.asConfirm(sel.id);
		if (r.success) { refresh(sel.id); load(); } else setMsg(r.message || '확인할 수 없습니다.');
	}

	const totalPages = Math.max(1, Math.ceil(total / size));

	return (
		<>
			{!embedded && <SubPageHero title="A/S 현황" subtitle="접수한 A/S의 진행상황을 추적합니다"
				image="/images/auth/member-hero.jpg" crumbs={[{ label: 'HOME' }, { label: '마이페이지' }, { label: 'A/S 현황' }]} />}
			<div className={s.wrap}>
				<div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
					<div>
						<p className={s.eyebrow}>MY A/S</p>
						<h2 className={s.h2}>A/S <b>현황</b></h2>
						<p className={s.lead}>택배 배송조회처럼 접수 → 원인분석 → 수리 → 완료 단계를 실시간으로 확인합니다.</p>
					</div>
					<button className={s.primary} style={{ width: 'auto', padding: '12px 22px' }} onClick={() => nav('/support/as/apply')}>+ A/S 신청</button>
				</div>
				{/* [2026-09-12 사용자] 안전/환경 단계 차이 안내(부제 아래). 공용 AsFlowLegend. */}
				{null}
				{msg && <div className={`${s.msg} ${s.msgErr}`}>{msg}</div>}

				<div className={s.filterBar}>
					<span className={s.filterLabel}>조회기간</span>
					{[{ v: '', t: '전체' }, { v: '1', t: '1개월' }, { v: '3', t: '3개월' }, { v: '6', t: '6개월' }].map((p) => (
						<button key={p.v} type="button" className={`${s.chip} ${period === p.v ? s.chipOn : ''}`} onClick={() => pickPeriod(p.v)}>{p.t}</button>
					))}
					<input className={s.dateInput} type="date" value={from} max={to || undefined} onChange={(e) => { setFrom(e.target.value); setPeriod(''); setPage(0); }} />
					<span style={{ color: '#6b7480' }}>~</span>
					<input className={s.dateInput} type="date" value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); setPeriod(''); setPage(0); }} />
				</div>

				<div className={s.list}>
					{rows.length === 0 ? <div className={s.empty}>{loading ? '불러오는 중…' : '접수한 A/S가 없습니다.'}</div>
						: rows.map((r) => {
							// [2026-09-13] 케이스별 활성 단계만 미니 스텝으로. cur=활성단계 내 현재 위치.
							const steps = activeStageKeys(parsePlan(r.stage_plan));
							const cur = steps.indexOf(r.status);
							return (
								<div className={s.item} key={r.id} onClick={() => open(r.id)}>
									<div className={s.itemTop}>
										<AsDomainBadge domain={r.domain} />
										<AsStatusBadge status={r.status} domain={r.domain} />
										<span className={s.itemNo}>AS-{String(r.id).padStart(6, '0')}</span>
									</div>
									<div className={s.itemName}>{r.product_name}</div>
										{/* [2026-09-12 사용자] 카드에 신청일(+완료 시 완료일) 표기 — 택배 배송조회식. 8단계라 뺀 게 아니라 누락이었음. */}
										<div style={{ fontSize: 12.5, color: '#8a94a0', margin: '3px 0 0', fontVariantNumeric: 'tabular-nums' }}>
											신청 {String(r.created_at).slice(0, 10)}{r.completed_at ? <> · 완료 {String(r.completed_at).slice(0, 10)}</> : ''}
										</div>
										<span className={s.itemHint} aria-hidden>자세히 보기 ›</span>
										{(r.status === 'CANCELED' || r.status === 'ON_HOLD') && <div className={s.miniCap}>{r.status === 'CANCELED' ? '취소됨' : '보류 중'}</div>}
									{r.status !== 'CANCELED' && r.status !== 'ON_HOLD' && !r.plan_locked_at && <div className={s.miniCap} style={{ color: '#0b6f96' }}>접수 완료 · 담당자 확인 중</div>}
										{r.status !== 'CANCELED' && r.status !== 'ON_HOLD' && r.plan_locked_at && (
										<div className={s.miniStepper}>
											{steps.map((st, i) => (
												<span key={st} className={s.msCol}>
													<span className={`${s.miniSeg} ${(i <= cur || r.status === 'COMPLETED') ? s.miniSegDone : ''}`} />
													<span className={`${s.miniLabel} ${(i === cur && r.status !== 'COMPLETED') ? s.miniLabelCur : (i <= cur || r.status === 'COMPLETED') ? s.miniLabelDone : ''}`}>{asLabel(st)}</span>
												</span>
											))}
										</div>
									)}
								</div>
							);
						})}
				</div>

				{rows.length > 0 && (
					<div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', marginTop: 24 }}>
						<button className={s.ghost} disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>이전</button>
						<span style={{ fontSize: 13, color: '#6b7480' }}>{page + 1} / {totalPages} · 총 {total}건</span>
						<button className={s.ghost} disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>다음</button>
					</div>
				)}
			</div>

			{sel && (
				<div className={s.modalBack} data-lenis-prevent onClick={() => setSel(null)}>
					<div className={s.modal} onClick={(e) => e.stopPropagation()}>
						<div className={s.modalHead}>
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								<AsDomainBadge domain={sel.domain} />
								<span className={s.asNo}>AS-{String(sel.id).padStart(6, '0')}</span>
							</div>
							<button className={s.mClose} onClick={() => setSel(null)} aria-label="닫기">✕</button>
						</div>
						<div className={s.modalBody}>
							<AsHero status={sel.status} domain={sel.domain} steps={activeStageKeys(parsePlan(sel.stage_plan))} product={sel.product_name} createdAt={sel.created_at} locked={!!sel.plan_locked_at} />

							{sel.plan_locked_at
									? <AsStepper status={sel.status} steps={activeStageKeys(parsePlan(sel.stage_plan))} />
									: (sel.status !== 'CANCELED' && <div style={{ fontSize: 14, color: '#33465e', background: '#eef6fd', border: '1px solid #cfe6f8', borderRadius: 12, padding: '16px 18px', lineHeight: 1.6 }}><b style={{ color: '#0b6f96' }}>접수 완료</b> — 담당자가 확인 후 처리 단계를 시작하면 여기에서 진행 상황을 단계별로 안내해 드립니다.</div>)}

							<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px', margin: '22px 0 18px' }}>
								<Info k="제품" v={sel.product_name} />
								<Info k="모델명" v={sel.product_model || '-'} />
								<Info k="현재 상태" v={asLabel(sel.status, sel.domain)} />
								<Info k="회사명" v={sel.company_name || '-'} />
								<Info k="신청자" v={sel.contact_name || '-'} />
								{sel.install_date && <Info k="설치일" v={String(sel.install_date).slice(0, 10)} />}
								{sel.purchase_source && <Info k="구매처" v={sel.purchase_source} />}
								{sel.visit_date && <Info k="처리 예정일" v={String(sel.visit_date).slice(0, 10)} />}
								{sel.technician && <Info k="담당 기사" v={sel.technician} />}
								{sel.warranty && <Info k="보증" v={warrantyLabel(sel.warranty)} />}
							</div>
							{/* [2026-09-12 폼개편] 주소=전체폭 한 줄(길 수 있음). */}
							<div style={{ fontSize: 13.5, color: '#4a5361', margin: '0 0 16px' }}>
								<span style={{ color: '#8894a0' }}>주소</span> &nbsp; {sel.address || '-'}
							</div>
							<div style={{ fontSize: 14.5, color: '#4a5361', background: '#f6f8fb', borderRadius: 10, padding: '14px 16px', marginBottom: 20, lineHeight: 1.65 }}>
								<b style={{ color: '#1a2230' }}>증상</b> · {sel.symptom}
							</div>

							{sel.status === 'COMPLETED' && (sel.confirmed_at ? (
									<div style={{ fontSize: 14, color: '#0f6e4e', background: '#e9f7f0', border: '1px solid #bfe6d3', borderRadius: 12, padding: '14px 16px', marginBottom: 20, lineHeight: 1.6 }}>✓ <b>수령·완료를 확인하셨습니다</b> · {dt(sel.confirmed_at)}</div>
								) : (
									<div style={{ background: '#eef6fd', border: '1px solid #cfe6f8', borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
										<div style={{ fontSize: 14, color: '#1a2230', fontWeight: 700, marginBottom: 4 }}>처리가 완료되었습니다.</div>
										<div style={{ fontSize: 13, color: '#4a5361', marginBottom: 12, lineHeight: 1.6 }}>제품·서비스를 수령·확인하셨다면 아래 버튼으로 완료를 확인해 주세요. (관리자 완료와 별개로 고객 확인 기록이 남습니다.)</div>
										<button className={s.primary} style={{ width: 'auto', padding: '11px 24px' }} onClick={doConfirm}>수령·완료 확인</button>
									</div>
								))}
								<div style={{ fontSize: 13, color: '#33465e', background: '#eef6fd', border: '1px solid #cfe6f8', borderRadius: 10, padding: '12px 15px', marginBottom: 22, lineHeight: 1.6 }}>
								A/S 관련 추가 문의는 <b style={{ color: '#0b6f96' }}>대표번호 {company.tel}</b>로 연락 주세요.
							</div>
							<div style={{ fontSize: 15, fontWeight: 700, color: '#1a2230', margin: '4px 0 4px' }}>진행 이력</div>
							<div style={{ fontSize: 12, color: '#6b7480', margin: '0 0 12px', lineHeight: 1.5 }}>담당자·본인이 남긴 처리 기록이 시간순으로 쌓입니다.</div>
							<AsTimeline rows={logs} domain={sel.domain} />

							{sel.status !== 'COMPLETED' && sel.status !== 'CANCELED' && (
								<>
									<div className={s.msgBox}>
										<input className={s.input} value={note} onChange={(e) => setNote(e.target.value)}
											onKeyDown={(e) => { if (e.key === 'Enter') sendMsg(); }} placeholder="추가로 남길 내용이 있으면 입력하세요 (Enter)" />
										<button className={s.ghost} onClick={sendMsg} disabled={!note.trim()}>등록</button>
									</div>
									{/* [2026-09-12 사용자] 입력창 안내: 남긴 글은 관리자와 공유(공유 타임라인)되고, 단계는 바뀌지 않음(단계 변경=담당자). 고객 혼동 방지. */}
									<div style={{ fontSize: 12, color: '#6b7480', margin: '7px 2px 0', lineHeight: 1.55 }}>
										남긴 내용은 <b style={{ color: '#6b7480' }}>담당 관리자에게 전달</b>되어 위 진행 이력에 함께 표시됩니다 · <b style={{ color: '#6b7480' }}>진행 단계는 바뀌지 않습니다</b>(단계 변경은 담당자가 진행).
									</div>
								</>
							)}
							{sel.status !== 'COMPLETED' && sel.status !== 'CANCELED' && (
								<div className={s.rowBtns}>
									<button className={s.ghost} onClick={() => setCancelOpen(true)}>신청 취소</button>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
			<ConfirmModal open={cancelOpen} danger title="A/S 신청 취소" confirmText="취소하기"
				message="이 A/S 신청을 취소하시겠습니까?" onConfirm={doCancel} onCancel={() => setCancelOpen(false)} />
		</>
	);
}

function Info({ k, v }: { k: string; v: string }) {
	return (
		<div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eef0f3', padding: '9px 0' }}>
			<span style={{ color: '#6b7480', fontSize: 14 }}>{k}</span>
			<span style={{ fontSize: 14.5, color: '#1a2230', fontWeight: 500, textAlign: 'right' }}>{v}</span>
		</div>
	);
}

export default observer(MyAs);
